export type PickLockMode = "gameStart" | "roundStart" | "afterFirstNGames";

export const PICK_LOCKED_ERROR = "Picks are locked because the game has already started.";

export interface PickLockPolicy {
  mode: PickLockMode;
  gamesCount?: number;
}

export const DEFAULT_PICK_LOCK_POLICY: PickLockPolicy = { mode: "gameStart" };

type UnknownRecord = Record<string, unknown>;

const asRecord = (value: unknown): UnknownRecord | null =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as UnknownRecord) : null;

export const normalizePickLockPolicy = (scoringConfig: unknown): PickLockPolicy => {
  const config = asRecord(scoringConfig);
  const rawPolicy = asRecord(config?.["pickLockPolicy"]);
  const rawMode = rawPolicy?.["mode"];

  if (rawMode === "roundStart") {
    return { mode: "roundStart" };
  }

  if (rawMode === "afterFirstNGames") {
    const rawCount = rawPolicy?.["gamesCount"];
    const numericCount =
      typeof rawCount === "number"
        ? rawCount
        : typeof rawCount === "string"
          ? Number.parseInt(rawCount, 10)
          : NaN;
    return {
      mode: "afterFirstNGames",
      gamesCount: Number.isFinite(numericCount) && numericCount > 0 ? Math.floor(numericCount) : 1,
    };
  }

  return DEFAULT_PICK_LOCK_POLICY;
};

const toValidDate = (value: Date | string | null | undefined): Date | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const sortedGameTimes = (values: Array<Date | string | null | undefined>): Date[] =>
  values
    .map(toValidDate)
    .filter((value): value is Date => Boolean(value))
    .sort((a, b) => a.getTime() - b.getTime());

const earliest = (a: Date | null, b: Date | null): Date | null => {
  if (!a) return b;
  if (!b) return a;
  return a.getTime() <= b.getTime() ? a : b;
};

/**
 * Resolves when a pick locks. A pick never stays open past its own game's kickoff,
 * even when an explicit bet cutoff or the season policy would lock the round later.
 */
export const resolvePickLockCutoff = ({
  policy,
  gameTime,
  roundGameTimes,
  explicitCutoffAt,
}: {
  policy: PickLockPolicy;
  gameTime: Date | string | null | undefined;
  roundGameTimes?: Array<Date | string | null | undefined>;
  explicitCutoffAt?: Date | string | null | undefined;
}): Date | null =>
  earliest(resolvePolicyCutoff({ policy, gameTime, roundGameTimes, explicitCutoffAt }), toValidDate(gameTime));

const resolvePolicyCutoff = ({
  policy,
  gameTime,
  roundGameTimes,
  explicitCutoffAt,
}: {
  policy: PickLockPolicy;
  gameTime: Date | string | null | undefined;
  roundGameTimes?: Array<Date | string | null | undefined>;
  explicitCutoffAt?: Date | string | null | undefined;
}): Date | null => {
  const explicitCutoff = toValidDate(explicitCutoffAt);
  if (explicitCutoff) {
    return explicitCutoff;
  }

  const fallbackGameTime = toValidDate(gameTime);
  if (policy.mode === "gameStart") {
    return fallbackGameTime;
  }

  const gameTimes = sortedGameTimes(roundGameTimes?.length ? roundGameTimes : [gameTime]);
  if (gameTimes.length === 0) {
    return fallbackGameTime;
  }

  if (policy.mode === "roundStart") {
    return gameTimes[0] ?? fallbackGameTime;
  }

  const count = Number.isFinite(policy.gamesCount) && (policy.gamesCount ?? 0) > 0 ? policy.gamesCount ?? 1 : 1;
  const index = Math.min(Math.floor(count) - 1, gameTimes.length - 1);
  return gameTimes[index] ?? fallbackGameTime;
};

export const isPickLocked = ({
  policy,
  gameTime,
  roundGameTimes,
  explicitCutoffAt,
  now = new Date(),
}: {
  policy: PickLockPolicy;
  gameTime: Date | string | null | undefined;
  roundGameTimes?: Array<Date | string | null | undefined>;
  explicitCutoffAt?: Date | string | null | undefined;
  now?: Date;
}): boolean => {
  const cutoff = resolvePickLockCutoff({ policy, gameTime, roundGameTimes, explicitCutoffAt });
  return Boolean(cutoff && cutoff <= now);
};

/**
 * True once the earliest game in a league week has kicked off.
 * Used to hide bulk-clear once a week is underway, even if later games are still unlocked.
 */
export const hasLeagueWeekStarted = ({
  gameTimes,
  now = new Date(),
}: {
  gameTimes: Array<Date | string | null | undefined>;
  now?: Date;
}): boolean => {
  const firstGameTime = sortedGameTimes(gameTimes)[0];
  return Boolean(firstGameTime && firstGameTime.getTime() <= now.getTime());
};
